<!-- src/components/rota/RotaGrid.vue -->
<template>
  <div class="card" style="overflow: hidden">
    <!-- Toolbar -->
    <div
      class="card-content"
      style="padding: 12px 14px; border-bottom: 1px solid #e0e0e0"
    >
      <div
        style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        "
      >
        <div>
          <div style="font-weight: 700">Rota</div>

          <div class="grey-text text-darken-1" style="font-size: 12px">
            {{
              effectiveCanEdit
                ? "Click a cell to add a shift"
                : "Rota is locked (published)"
            }}
          </div>
        </div>

        <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap">
          <span
            v-if="published"
            class="chip green lighten-4 green-text text-darken-4"
            style="margin: 0"
            title="This rota has been published"
          >
            Published
          </span>

          <button
            v-if="showCopyPreviousWeek"
            type="button"
            class="btn teal darken-2"
            :disabled="!effectiveCanEdit || copyPreviousWeekBusy || !canCopyPreviousWeek"
            title="Copy last week's rota into this week"
            @click.prevent="emitCopyPreviousWeek"
          >
            <i class="material-icons left"> content_copy </i>

            {{ copyPreviousWeekBusy ? "Copying..." : "Copy previous week" }}
          </button>

          <button
            v-if="isOperations"
            type="button"
            class="btn pink darken-3"
            :disabled="pdfBusy"
            @click.prevent="emitGeneratePdf"
          >
            {{ pdfBusy ? "Generating..." : "Print rota" }}
          </button>

          <button
            v-if="!published"
            type="button"
            class="btn blue-grey darken-3"
            :disabled="publishing || !canPublish"
            @click.prevent="emitPublish(false)"
          >
            {{ publishing ? "Publishing..." : "Publish rota" }}
          </button>

          <button
            v-else
            type="button"
            class="btn blue-grey darken-3"
            :disabled="publishing || !canPublish"
            @click.prevent="emitPublish(true)"
          >
            {{ publishing ? "Republishing..." : "Republish changes" }}
          </button>

          <button
            v-if="published && !editEnabled"
            type="button"
            class="btn-flat"
            :disabled="publishing"
            title="Unlock editing for this rota"
            @click.prevent="emitEnableEdit"
          >
            Enable edit
          </button>
        </div>
      </div>

      <p v-if="publishError" class="red-text" style="margin: 10px 0 0">
        {{ publishError }}
      </p>
    </div>

    <!-- Grid -->
    <div class="card-content" style="padding: 0">
      <div class="rota-grid-wrap">
        <!-- FORECAST ROW: above Employees, aligned Monday to Sunday -->
        <div class="rota-grid forecast-row">
          <div class="cell sticky-col blue-grey lighten-5">
            <div style="font-weight: 700">Forecast</div>
            <div class="grey-text text-darken-1" style="font-size: 12px">
              Daily sales forecast
            </div>
          </div>

          <div
            v-for="(day, dayIndex) in weekDays"
            :key="'forecast-' + day.ymd"
            class="cell blue-grey lighten-5"
          >
            <div style="font-size: 15px; font-weight: 700">
              <span v-if="forecastLoading" class="grey-text">Loading...</span>
              <span v-else-if="forecastError" class="red-text">Unavailable</span>
              <span v-else>{{ formatForecast(forecastTotals[dayIndex]) }}</span>
            </div>
          </div>
        </div>

        <!-- HEADER ROW -->
        <div class="rota-grid header">
          <div class="cell sticky-col header-left">
            <div style="font-weight: 700">Employees</div>

            <div class="grey-text text-darken-1" style="font-size: 12px">
              {{
                effectiveCanEdit
                  ? "Click a cell to add a shift"
                  : "Rota is locked (published)"
              }}
            </div>
          </div>

          <div v-for="d in weekDays" :key="d.ymd" class="cell header-day">
            <div
              style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 10px;
              "
            >
              <div style="font-weight: 700">
                {{ d.label }}
              </div>

              <div class="grey-text text-darken-1" style="font-size: 12px">
                {{ dayShiftCount(d.ymd) }} shifts
              </div>
            </div>

            <div class="grey-text text-darken-2" style="font-size: 12px; margin-top: 4px">
              {{ Number(dayHoursTotal(d.ymd) || 0).toFixed(1) }} hrs
            </div>
          </div>
        </div>

        <!-- BODY -->
        <div v-for="s in staffRows" :key="s.id" class="rota-grid row">
          <!-- Left sticky staff column -->
          <div class="cell sticky-col staff-cell">
            <div
              style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 10px;
              "
            >
              <div>
                <div style="font-weight: 700">
                  {{ staffName(s) }}
                </div>

                <div class="grey-text text-darken-1" style="font-size: 12px">
                  {{ staffJobTitle(s) }}
                  · £{{ Number(staffPayRate(s)).toFixed(2) }}/hr
                </div>
              </div>

              <div class="chip" style="height: 24px; line-height: 24px; font-size: 12px">
                {{ Number(staffHoursForWeek(s.id) || 0).toFixed(1) }} hrs
              </div>
            </div>
          </div>

          <!-- Day cells -->
          <div
            v-for="d in weekDays"
            :key="s.id + '-' + d.ymd"
            class="cell day-cell"
            :class="{
              disabled: !effectiveCanEdit || holidayBlockFor(s, d.ymd),

              'holiday-blocked-cell': holidayBlockFor(s, d.ymd),
            }"
            @click.stop.prevent="holidayBlockFor(s, d.ymd) ? null : onCellClick(s, d.ymd)"
          >
            <div class="shift-stack">
              <!-- Holiday block -->
              <div
                v-if="holidayBlockFor(s, d.ymd)"
                class="holiday-block"
                :class="{
                  'holiday-block-clickable': effectiveCanEdit,
                }"
                :title="effectiveCanEdit ? 'Click to edit' : 'Approved leave'"
                @click.stop.prevent="onHolidayClick(s, d.ymd)"
              >
                <div class="holiday-title">
                  {{ holidayBlockFor(s, d.ymd).label || "Holiday" }}
                </div>

                <div class="holiday-subtitle">Approved leave</div>
              </div>

              <!-- Existing shifts -->
              <div
                v-for="sh in shiftsForCell(s.id, d.ymd)"
                :key="sh.id"
                class="shift-block"
                :class="roleClassFromShiftOrStaff(sh, s)"
                title="Click to edit"
                @click.stop.prevent="onShiftClick(sh)"
              >
                <div class="shift-title">
                  {{ sh.role || staffJobTitle(s) }}
                </div>

                <div class="shift-time">
                  {{ formatTimeLabel(sh.startTime) }}
                  -
                  {{ formatTimeLabel(sh.endTime) }}
                </div>
              </div>

              <!-- Add shift -->
              <button
                v-if="
                  effectiveCanEdit &&
                  !holidayBlockFor(s, d.ymd) &&
                  shiftsForCell(s.id, d.ymd).length === 0
                "
                type="button"
                class="empty-hint-btn"
                :disabled="!effectiveCanEdit"
                aria-label="Add shift"
                title="Add shift"
                @click.stop.prevent="onCellClick(s, d.ymd)"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="staffRows.length === 0" class="card-panel" style="margin: 12px">
        No staff found for this venue.
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase";
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

const props = defineProps({
  staffRows: {
    type: Array,
    default: () => [],
  },

  weekDays: {
    type: Array,
    default: () => [],
  },

  canEdit: {
    type: Boolean,
    default: true,
  },

  published: {
    type: Boolean,
    default: false,
  },

  editEnabled: {
    type: Boolean,
    default: false,
  },

  publishing: {
    type: Boolean,
    default: false,
  },

  publishError: {
    type: String,
    default: "",
  },

  canPublish: {
    type: Boolean,
    default: true,
  },

  pdfBusy: {
    type: Boolean,
    default: false,
  },

  isOperations: {
    type: Boolean,
    default: false,
  },

  showCopyPreviousWeek: {
    type: Boolean,
    default: true,
  },

  canCopyPreviousWeek: {
    type: Boolean,
    default: true,
  },

  copyPreviousWeekBusy: {
    type: Boolean,
    default: false,
  },

  shiftsForCell: {
    type: Function,
    required: true,
  },

  staffHoursForWeek: {
    type: Function,
    required: true,
  },

  dayShiftCount: {
    type: Function,
    required: true,
  },

  dayHoursTotal: {
    type: Function,
    required: true,
  },

  formatTimeLabel: {
    type: Function,
    required: true,
  },

  holidayBlockForCell: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits([
  "cell-click",
  "shift-click",
  "holiday-click",
  "publish",
  "enable-edit",
  "generate-pdf",
  "copy-previous-week",
]);

const effectiveCanEdit = computed(() => {
  if (!props.canEdit) {
    return false;
  }

  if (!props.published) {
    return true;
  }

  return props.editEnabled === true;
});

/* ─── Daily sales forecast (existing Forecast collection) ─── */
const { venueId } = useVenueContext();
const forecastTotals = ref(Array(7).fill(null));
const forecastLoading = ref(false);
const forecastError = ref("");

// Rota weeks start on Monday; Forecast document IDs use Sunday week-ending.
const forecastWeekEnding = computed(() => {
  const days = props.weekDays || [];
  return days.length === 7 ? String(days[6]?.ymd || "") : "";
});

function formatForecast(value) {
  if (value === null || value === undefined) return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return amount.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

watch(
  [venueId, forecastWeekEnding],
  ([selectedVenue, weekEnding], _previous, onCleanup) => {
    forecastTotals.value = Array(7).fill(null);
    forecastLoading.value = false;
    forecastError.value = "";

    const v = String(selectedVenue || "").trim();
    const we = String(weekEnding || "").trim();
    if (!v || !we) return;

    forecastLoading.value = true;
    let active = true;

    const unsubscribe = onSnapshot(
      collection(db, "venues", v, "forecast_weeks", we, "cells"),
      (snapshot) => {
        if (!active) return;

        const totals = Array(7).fill(0);
        const hasForecast = Array(7).fill(false);

        snapshot.forEach((forecastCell) => {
          const data = forecastCell.data() || {};
          const dayIndex = Number(data.day_index);
          const amount = Number(data.value);
          if (!Number.isInteger(dayIndex) || dayIndex < 0 || dayIndex > 6) return;
          if (!Number.isFinite(amount)) return;

          totals[dayIndex] += amount;
          hasForecast[dayIndex] = true;
        });

        forecastTotals.value = totals.map((amount, index) =>
          hasForecast[index] ? amount : null
        );
        forecastLoading.value = false;
        forecastError.value = "";
      },
      (error) => {
        if (!active) return;
        console.error("[RotaGrid] Unable to read forecast:", error);
        forecastTotals.value = Array(7).fill(null);
        forecastLoading.value = false;
        forecastError.value = error?.message || "Forecast unavailable";
      }
    );

    onCleanup(() => {
      active = false;
      unsubscribe();
    });
  },
  { immediate: true }
);

function emitGeneratePdf() {
  if (!props.isOperations) {
    return;
  }

  if (props.pdfBusy) {
    return;
  }

  emit("generate-pdf");
}

function emitCopyPreviousWeek() {
  if (!effectiveCanEdit.value) {
    return;
  }

  if (!props.canCopyPreviousWeek) {
    return;
  }

  if (props.copyPreviousWeekBusy) {
    return;
  }

  emit("copy-previous-week");
}

function staffName(s) {
  return String(s?.staff_name || s?.name || s?.staffName || "—");
}

function staffJobTitle(s) {
  return String(s?.job_title || s?.jobTitle || "—");
}

function staffPayRate(s) {
  return Number(s?.pay_rate ?? s?.payRate ?? 0);
}

function holidayBlockFor(staffRow, ymd) {
  if (typeof props.holidayBlockForCell !== "function") {
    return null;
  }

  return props.holidayBlockForCell(staffRow?.id, ymd);
}

function roleClassByTitle(title) {
  const t = String(title || "")
    .trim()
    .toLowerCase();

  if (t === "general manager") {
    return "role-gm";
  }

  if (t === "assistant manager") {
    return "role-am";
  }

  if (t === "supervisor") {
    return "role-sup";
  }

  if (t === "bartender") {
    return "role-bar";
  }

  if (t === "floorstaff") {
    return "role-floor";
  }

  if (t === "cleaner") {
    return "role-clean";
  }

  return "";
}

function roleClassFromStaff(staffRow) {
  return roleClassByTitle(staffJobTitle(staffRow));
}

function roleClassFromShiftOrStaff(shift, staffRow) {
  const shiftRole = shift?.role;

  if (shiftRole) {
    return roleClassByTitle(shiftRole);
  }

  return roleClassFromStaff(staffRow);
}

function onCellClick(staffRow, ymd) {
  if (!effectiveCanEdit.value) {
    return;
  }

  if (holidayBlockFor(staffRow, ymd)) {
    return;
  }

  emit("cell-click", {
    staffRow,
    ymd,
  });
}

function onShiftClick(shift) {
  if (!effectiveCanEdit.value) {
    return;
  }

  emit("shift-click", shift);
}

function onHolidayClick(staffRow, ymd) {
  if (!effectiveCanEdit.value) {
    return;
  }

  const holiday = holidayBlockFor(staffRow, ymd);

  if (!holiday) {
    return;
  }

  emit("holiday-click", {
    holiday,
    staffRow,

    staffId: staffRow?.id,

    ymd,
  });
}

function emitPublish(republish) {
  if (!props.canPublish || props.publishing) {
    return;
  }

  emit("publish", {
    republish: !!republish,
  });
}

function emitEnableEdit() {
  if (props.publishing) {
    return;
  }

  emit("enable-edit");
}
</script>

<style scoped>
.rota-grid-wrap {
  overflow: auto;
  max-height: calc(100vh - 260px);
}

.rota-grid {
  display: grid;
  grid-template-columns: 320px repeat(7, minmax(180px, 1fr));
  min-width: 1580px;
}

.cell {
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  padding: 10px;
  background: #fff;
}

/* Forecast row: vertically centred, left-aligned */
.forecast-row .cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
}

.header .cell {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #fafafa;
  border-bottom: 2px solid #e0e0e0;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 6;
  background: #fff;
  border-right: 2px solid #e0e0e0;
}

.header-left {
  background: #fafafa !important;
}

.staff-cell {
  background: #fff;
}

.day-cell {
  cursor: pointer;
  min-height: 86px;
}

.day-cell.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.holiday-blocked-cell {
  background: #eeeeee !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
}

.shift-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shift-block {
  background: #f2f2f2;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
}

.shift-title {
  font-weight: 700;
  font-size: 12px;
  color: inherit;
}

.shift-time {
  font-size: 12px;
  color: inherit;
  opacity: 0.9;
  margin-top: 2px;
}

.empty-hint-btn {
  width: 100%;
  height: 28px;
  border: 1px dashed #cfcfcf;
  border-radius: 8px;
  color: #9e9e9e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  background: transparent;
  cursor: pointer;
}

.empty-hint-btn:disabled {
  cursor: not-allowed;
}

.holiday-block {
  border-radius: 8px;
  padding: 10px;
  background: #cfd8dc;
  border: 1px solid #b0bec5;
  color: #263238;
}

.holiday-block-clickable {
  cursor: pointer;
}

.holiday-block-clickable:hover {
  filter: brightness(0.97);
}

.holiday-title {
  font-weight: 900;
  font-size: 13px;
  line-height: 1.2;
}

.holiday-subtitle {
  margin-top: 3px;
  font-size: 12px;
  opacity: 0.8;
}

.shift-block.role-gm {
  background: #d32f2f;
  border-color: #d32f2f;
  color: #fff;
}

.shift-block.role-am {
  background: #f57c00;
  border-color: #f57c00;
  color: #fff;
}

.shift-block.role-sup {
  background: #1976d2;
  border-color: #1976d2;
  color: #fff;
}

.shift-block.role-bar {
  background: #388e3c;
  border-color: #388e3c;
  color: #fff;
}

.shift-block.role-floor {
  background: #7b1fa2;
  border-color: #7b1fa2;
  color: #fff;
}

.shift-block.role-clean {
  background: #00796b;
  border-color: #00796b;
  color: #fff;
}

@media (max-width: 600px) {
  .card > .card-content:first-child {
    padding: 10px 12px !important;
  }

  .card > .card-content:first-child > div {
    align-items: stretch !important;
  }

  .card > .card-content:first-child > div > div:last-child {
    width: 100%;
    display: grid !important;
    grid-template-columns: 1fr;
    gap: 8px !important;
  }

  .card > .card-content:first-child .chip {
    justify-self: stretch;
    text-align: center;
  }

  .rota-grid-wrap {
    max-height: calc(100vh - 230px);
    -webkit-overflow-scrolling: touch;
  }

  .rota-grid {
    grid-template-columns: 240px repeat(7, minmax(150px, 1fr));
    min-width: 1290px;
  }

  .cell {
    padding: 8px;
  }

  .day-cell {
    min-height: 78px;
  }

  .shift-block,
  .holiday-block {
    padding: 7px;
  }
}
</style>
